import React, { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormDateTime, { FormDateTimeModifier } from '../../Form/components/FormDateTime/FormDateTime';
import { DateFormat } from '../../../../_lib/components/DateFormat';
import { Namespace } from '../../../../translation/namespace';
import FormsRow, { FormsRowModifier } from '../../Form/components/FormsRow/FormsRow';
import { sendLiveRegionMessage } from '../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import FormSubmitOrCancel from '../../Form/components/FormSubmitOrCancel/FormSubmitOrCancel';
import { CollapsibleContent } from '../../../organisms/CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../../hooks/useCollapsibleOverlay';
import { FormInputModifier } from '../../Form/components/FormInput/FormInput.modifier';
import { isDateAfter, isDateBefore } from '../../../../lib/DateFormat';
import { FilterButton } from './FilterButton';
import { FilterContainer } from './FilterContainer';

export type DateFilter = {
  name: string;
  value: string;
  required: boolean;
};

type FilterDateProps = {
  active: boolean;
  onSubmit: (query: Record<string, string>) => void;
  facetKey: string;
  facetType: string;
  startDate: DateFilter;
  endDate: DateFilter;
  query: Record<string, string>;
};

export function FilterDate({ active, onSubmit, facetKey, facetType, startDate, endDate, query }: FilterDateProps) {
  const [dates, setDates] = useState({ startDate: startDate.value, endDate: endDate.value });
  const { t } = useTranslation(`${Namespace.ResultListSettings}`);
  const { collapsibleOpen, focusToggle, toggleCollapsible, toggleProps, collapsibleProps, popperInstance } =
    useCollapsibleOverlay('bottom');

  const handleSubmit = () => {
    // creating the query object needed for filtering
    onSubmit({
      ...query,
      [facetKey]: facetType,
      [startDate.name]: dates.startDate,
      [endDate.name]: dates.endDate,
    });
  };

  const acceptAction = (event: MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    handleSubmit();
    toggleCollapsible();
  };

  const closeAndFocusButton = () => {
    toggleCollapsible();
    setDates({ startDate: startDate.value, endDate: endDate.value });
    if (toggleProps.ref.current) {
      focusToggle();
    }
  };

  const getActiveTitle = () => {
    return active ? (
      <>
        <DateFormat date={startDate.value} /> {t('facets.fq_contentDate.to_small')}&nbsp;
        <DateFormat date={endDate.value} />
      </>
    ) : null;
  };

  /**
   * checks if dates are out of range (start after end, end before start). if so we set them to the same value,
   * this way users can not easily create a invalid date range.
   * @param name startDate and endDate, is the same as in state object
   * @param value time value we get from inputs, always in yyyy-MM-dd
   */
  const setDatesAndCheckRange = (name: string, value: string) => {
    setDates(prv => {
      if (name === 'startDate' && isDateAfter(value, prv.endDate)) {
        sendLiveRegionMessage(t('facets.fq_contentDate.liveRegionMessage.startAfterEnd'));
        return { startDate: value, endDate: value };
      }
      if (name === 'endDate' && isDateBefore(value, prv.startDate)) {
        sendLiveRegionMessage(t('facets.fq_contentDate.liveRegionMessage.endBeforeStart'));
        return { startDate: value, endDate: value };
      }
      return { ...prv, [name]: value };
    });
  };

  useEffect(() => {
    if (collapsibleOpen && popperInstance) {
      popperInstance.update();
    }
  });

  return (
    <FilterContainer>
      <FilterButton
        title={t('facets.fq_contentDate.title')}
        value={getActiveTitle()}
        isActive={collapsibleOpen || active}
        buttonProps={toggleProps}
      />
      <CollapsibleContent {...collapsibleProps}>
        <div className="filter-button__panel-container">
          <FormsRow>
            <FormDateTime
              modifier={FormDateTimeModifier.FULL_WIDTH}
              modifierInput={FormInputModifier.INVERTED}
              label={t('facets.fq_contentDate.from')}
              name={startDate.name}
              value={dates.startDate}
              required={startDate.required}
              onBlur={(e: FocusEvent) => setDatesAndCheckRange('startDate', (e.target as HTMLInputElement).value)}
            />
          </FormsRow>
          <FormsRow modifier={FormsRowModifier.NO_MARGIN}>
            <FormDateTime
              modifier={FormDateTimeModifier.FULL_WIDTH}
              modifierInput={FormInputModifier.INVERTED}
              label={t('facets.fq_contentDate.to')}
              name={endDate.name}
              value={dates.endDate}
              required={endDate.required}
              onBlur={(e: FocusEvent) => setDatesAndCheckRange('endDate', (e.target as HTMLInputElement).value)}
            />
          </FormsRow>
        </div>
        <div className="filter-button__panel-interactions">
          <FormSubmitOrCancel
            acceptAction={acceptAction}
            cancelAction={closeAndFocusButton}
            headline={t('filterButton.headline')}
            hierarchy={2}
            hideHeadline
          />
        </div>
      </CollapsibleContent>
    </FilterContainer>
  );
}
